import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useDeliveryComparison() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/delivery/history`, { headers });
      setHistory(res.data);
    } catch { } finally { setIsLoadingHistory(false); }
  }, [token]);

  useEffect(() => { if (token) fetchHistory(); }, [token, fetchHistory]);

  const compare = useCallback(async (projectId, ddnFiles, dmlFiles) => {
    setIsComparing(true);
    try {
      const res = await axios.post(`${API}/delivery/compare`, {
        project_id: projectId === 'none' ? null : projectId,
        ddn_files: ddnFiles, dml_files: dmlFiles
      }, { headers });
      setComparisonResult(res.data.result);
      fetchHistory();
      toast.success('Comparison completed');
      return true;
    } catch {
      toast.error('Comparison failed');
      return false;
    } finally { setIsComparing(false); }
  }, [token, fetchHistory]);

  const viewDetail = useCallback(async (historyItem) => {
    try {
      const res = await axios.get(`${API}/delivery/history/${historyItem.id}`, { headers });
      setSelectedHistory(res.data);
      setComparisonResult({
        ddn_entries: res.data.ddn_entries, dml_entries: res.data.dml_entries,
        matches: res.data.matches, mismatches: res.data.mismatches || [],
        in_ddn_only: res.data.in_ddn_only,
        in_dml_only: res.data.in_dml_only,
        total_ddn: res.data.ddn_entries?.length || 0,
        total_dml: res.data.dml_entries?.length || 0,
        match_percentage: res.data.match_percentage
      });
      return true;
    } catch { toast.error('Failed to load history detail'); return false; }
  }, [token]);

  const deleteItem = useCallback(async (historyId) => {
    try {
      await axios.delete(`${API}/delivery/history/${historyId}`, { headers });
      toast.success('History deleted');
      fetchHistory();
    } catch { toast.error('Failed to delete history'); }
  }, [token, fetchHistory]);

  const clearResult = useCallback(() => {
    setComparisonResult(null);
    setSelectedHistory(null);
  }, []);

  // CSV export helpers
  const csvEscape = useCallback((val) => {
    const s = String(val ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  }, []);

  const generateReport = useCallback((type, result, projectName) => {
    const ts = new Date().toISOString();
    const rows = [];
    if (type === 'all') {
      rows.push(['DDN/DML Comparison Report']);
      rows.push(['Generated', ts]);
      rows.push(['Project', projectName || 'Not specified']);
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total DDN Entries', result.total_ddn || result.ddn_entries?.length || 0]);
      rows.push(['Total DML Entries', result.total_dml || result.dml_entries?.length || 0]);
      rows.push(['Matching Entries', result.matches?.length || 0]);
      rows.push(['Issue/Language Mismatches', result.mismatches?.length || 0]);
      rows.push(['DDN Only', result.in_ddn_only?.length || 0]);
      rows.push(['DML Only', result.in_dml_only?.length || 0]);
      rows.push(['Match Rate %', result.match_percentage]);
      rows.push([]);
      rows.push(['Entry', 'Status']);
      (result.matches || []).forEach(e => rows.push([e, 'Match']));
      (result.mismatches || []).forEach(e => rows.push([e, 'Issue/Language Mismatch']));
      (result.in_ddn_only || []).forEach(e => rows.push([e, 'DDN Only']));
      (result.in_dml_only || []).forEach(e => rows.push([e, 'DML Only']));
    } else if (type === 'ddn') {
      rows.push(['DDN Only Entries Report']);
      rows.push(['Generated', ts]);
      rows.push(['Project', projectName || 'Not specified']);
      rows.push(['Total DDN Only Entries', result.in_ddn_only?.length || 0]);
      rows.push([]);
      rows.push(['Entry']);
      (result.in_ddn_only || []).forEach(e => rows.push([e]));
    } else {
      rows.push(['DML Only Entries Report']);
      rows.push(['Generated', ts]);
      rows.push(['Project', projectName || 'Not specified']);
      rows.push(['Total DML Only Entries', result.in_dml_only?.length || 0]);
      rows.push([]);
      rows.push(['Entry']);
      (result.in_dml_only || []).forEach(e => rows.push([e]));
    }
    return rows.map(r => r.map(c => csvEscape(c)).join(',')).join('\n');
  }, [csvEscape]);

  const downloadExport = useCallback((type, projectName) => {
    if (!comparisonResult) return;
    const content = generateReport(type, comparisonResult, projectName);
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().split('T')[0];
    a.download = { all: `comparison_report_${ts}.csv`, ddn: `ddn_only_${ts}.csv`, dml: `dml_only_${ts}.csv` }[type];
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${type === 'all' ? 'Full report' : type.toUpperCase() + ' only report'} downloaded`);
  }, [comparisonResult, generateReport]);

  const exportHistoryItem = useCallback(async (historyItem) => {
    try {
      const res = await axios.get(`${API}/delivery/history/${historyItem.id}`, { headers });
      const d = res.data;
      const result = {
        ddn_entries: d.ddn_entries, dml_entries: d.dml_entries, matches: d.matches,
        in_ddn_only: d.in_ddn_only, in_dml_only: d.in_dml_only,
        total_ddn: d.ddn_entries?.length || 0, total_dml: d.dml_entries?.length || 0,
        match_percentage: d.match_percentage
      };
      const content = generateReport('all', result, d.project_title);
      const blob = new Blob([content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comparison_report_${new Date(d.created_at).toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch { toast.error('Failed to export report'); }
  }, [token, generateReport]);

  return {
    isComparing, comparisonResult, history, isLoadingHistory, selectedHistory,
    compare, viewDetail, deleteItem, clearResult,
    downloadExport, exportHistoryItem
  };
}
